import { adminMutation } from "@/admin/utils/adminMutation.ts";
import { type ApiClient, getResponseData } from "@/modules/api/apiClient.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { type QueryKey, queryOptions } from "@tanstack/react-query";
import type {
    AdminMetalLeaseCreateBody,
    AdminMetalLeaseUpdateBody,
    AdminMetalMachineCreateBody,
    AdminMetalMachineItem,
    AdminMetalMachineUpdateBody,
} from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries } from "@warrenio/utils/collections/maps";
import { atomFamily } from "jotai-family";

//#region Response

/** Bare metal with extra `type` property */
export interface AdminMetalMachineItemLoc extends AdminMetalMachineItem {
    $type: "metal_machine";
    location: string;
}

function metalFromData(data: AdminMetalMachineItem, location: string): AdminMetalMachineItemLoc {
    return { ...data, $type: "metal_machine", location };
}

type Response = Map<AdminMetalMachineItemLoc["uuid"], AdminMetalMachineItemLoc>;

//#endregion

//#region Query and parameters
function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["metal_machine/list"] : ["metal_machine/list", params.location];
}

interface Params {
    location: string;
}

function getQuery(client: ApiClient, params: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }): Promise<Response> => {
            const { location } = params;
            return mapFromEntries(
                getResponseData(
                    await client.GET("/{location}/admin/metal/machines", {
                        signal,
                        params: { path: { location } },
                    }),
                ),
                (metal) => [metal.uuid, metalFromData(metal, location)] as const,
            );
        },
    });
}
//#endregion

//#region Atoms
export const metalMachineLocationQueryAtom = atomFamily((location: string) =>
    atomFromStandardQueryOptions(getQuery, { location }),
);

//#endregion

//#region Mutations

const entity = "Machine";

export const machineCreateMutation = adminMutation((apiClient) => ({
    entity,
    operation: "create",
    requestFn: async ({ body, location }: { body: AdminMetalMachineCreateBody; location: string }) =>
        await apiClient.POST("/{location}/admin/metal/machines", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
    invalidateQueryKey: getQueryKey,
}));

export const machineUpdateMutation = adminMutation((apiClient) => ({
    entity,
    operation: "update",
    requestFn: async ({
        body,
        location,
        uuid,
    }: {
        body: AdminMetalMachineUpdateBody;
        location: string;
        uuid: string;
    }) =>
        await apiClient.PATCH("/{location}/admin/metal/machines/{uuid}", {
            ...jsonEncodedBody,
            body,
            params: { path: { location, uuid } },
        }),
    invalidateQueryKey: getQueryKey,
}));

export const machineDeleteMutation = adminMutation((apiClient) => ({
    entity,
    operation: "delete",
    requestFn: async ({ location, uuid }: { location: string; uuid: string }) =>
        await apiClient.DELETE("/{location}/admin/metal/machines/{uuid}", {
            params: { path: { location, uuid } },
        }),
    invalidateQueryKey: getQueryKey,
}));

//#endregion

//#region Lease mutations

const leaseEntity = "Lease";

export const leaseCreateMutation = adminMutation((apiClient) => ({
    entity: leaseEntity,
    operation: "create",
    requestFn: async ({ body, location }: { body: AdminMetalLeaseCreateBody; location: string }) =>
        await apiClient.POST("/{location}/admin/metal/leases", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
    invalidateQueryKey: getQueryKey,
}));

export const leaseUpdateMutation = adminMutation((apiClient) => ({
    entity: leaseEntity,
    operation: "update",
    requestFn: async ({ body, location, uuid }: { body: AdminMetalLeaseUpdateBody; location: string; uuid: string }) =>
        await apiClient.PATCH("/{location}/admin/metal/leases/{uuid}", {
            ...jsonEncodedBody,
            body,
            params: { path: { location, uuid } },
        }),
    invalidateQueryKey: getQueryKey,
}));

//#endregion
